/**
 * Swamithra — the user's queries (US-00-002).
 *
 * OWNERSHIP IS NEVER A PARAMETER. Nothing here takes a user id: the server
 * resolves the owner from the session, and a request for someone else's query by
 * id comes back as the same 403 whether or not that id exists (US-00-006.1/.19,
 * US-00-006.2). A client that could pass a user id is a client that could be
 * made to pass the wrong one.
 *
 * THERE IS NO UPDATE AND NO DELETE. US-00-002.25 requires that the API expose no
 * route to edit or remove a submitted query's title, category, description or
 * attachments; the absence of those functions from this module is that
 * requirement, and it is why adding one would be a contract change rather than a
 * convenience.
 */

import { referenceParam } from "../domain/ids"
import { type PageParams, pageQuery, paginated } from "../domain/pagination"
import {
	type CreateQueryInput,
	createQueryInputSchema,
	type FollowUpInput,
	followUpInputSchema,
	type MyQueriesFilters,
	queryDetailSchema,
	type QueryFeedbackInput,
	queryFeedbackInputSchema,
	queryFeedbackSchema,
	queryMessageSchema,
	querySummarySchema,
} from "../domain/query"
import { appendFiles, type PlatformFile } from "../files"
import { api } from "./client"
import { type ReadOptions, type SubmitOptions } from "./options"

const myQueriesPage = paginated(querySummarySchema)

/**
 * My Queries, most recent first.
 *
 * The ordering is the server's (US-00-002.6) rather than a sort the client
 * applies, because a client-side sort only orders the page it happens to hold.
 */
export function fetchMyQueries(
	filters: MyQueriesFilters = {},
	page: PageParams = {},
	options: ReadOptions = {},
) {
	return api.get("/queries", myQueriesPage, {
		...options,
		query: { ...pageQuery(page), ...(filters.status ? { status: filters.status } : {}) },
	})
}

/**
 * One query with its thread.
 *
 * `queryRef` IS THE HUMAN-READABLE NUMBER, not the UUID: `/queries/{queryRef}`
 * takes "12" or "#12" and the opaque id addresses nothing (US-00-002.30). A
 * request for someone else's — including a guessed one — is the same 403 as a
 * request for one that does not exist (US-00-006.19).
 */
export function fetchQuery(queryRef: string, options: ReadOptions = {}) {
	return api.get(`/queries/${referenceParam(queryRef)}`, queryDetailSchema, options)
}

/**
 * Submits a query with up to three attachments.
 *
 * Multipart, because the files and the fields are one atomic submission: a
 * two-step upload-then-create leaves orphaned objects in the bucket every time a
 * user closes the app between the steps.
 *
 * The caller validates the files with `validateFileSet` first — this only sends
 * them. The server sniffs the content type and returns 422 for a spoofed
 * extension regardless (US-00-002.2).
 */
export async function createQuery(
	input: CreateQueryInput,
	attachments: readonly PlatformFile[],
	options: SubmitOptions,
) {
	const parsed = createQueryInputSchema.parse(input)
	const form = new FormData()
	form.append("title", parsed.title)
	form.append("description", parsed.description)
	appendFiles(form, "attachments", attachments)

	return await api.post("/queries", queryDetailSchema, form, {
		signal: options.signal,
		idempotencyKey: options.idempotencyKey,
	})
}

/**
 * Posts a follow-up into an existing thread.
 *
 * Allowed on Open, In Progress and Answered. On a Closed query the composer is
 * hidden and this returns 409 (US-00-002.12, US-01-003.9) — the 409 is the
 * enforcement, the hidden composer is the courtesy.
 */
export async function postFollowUp(
	queryRef: string,
	input: FollowUpInput,
	attachments: readonly PlatformFile[],
	options: SubmitOptions,
) {
	const parsed = followUpInputSchema.parse(input)
	const form = new FormData()
	form.append("body", parsed.body)
	appendFiles(form, "attachments", attachments)

	return await api.post(
		`/queries/${referenceParam(queryRef)}/messages`,
		queryMessageSchema,
		form,
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}

/**
 * Rates an answered query.
 *
 * THE RESPONSE IS THE FEEDBACK, NOT THE QUERY — 201 with the rating, the comment
 * and when it was recorded. Submitting it also CLOSES the query in the same
 * transaction (US-01-003.2), so the caller has to refetch the query rather than
 * read a new status out of this; the hook that owns the cache does exactly that.
 * A second submission returns 409 and changes nothing (US-00-002.14).
 */
export async function submitFeedback(
	queryRef: string,
	input: QueryFeedbackInput,
	options: SubmitOptions,
) {
	return await api.post(
		`/queries/${referenceParam(queryRef)}/feedback`,
		queryFeedbackSchema,
		queryFeedbackInputSchema.parse(input),
		{ signal: options.signal, idempotencyKey: options.idempotencyKey },
	)
}
